import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import Button from 'components/Form/Button';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { POST } from 'services/api';
import { isOmni } from 'utils/validators';
import {
  AddProposalContainer,
  AnalysisCard,
  AnalysisContent,
  AnalysisIcon,
  AnalysisItem,
  AnalysisResultContainer,
  AnalysisSection,
  AnalysisSectionTitle,
  AnalysisText,
  AnalysisTitle,
  CompanyButtonContainer,
  FormSelect,
  Loader,
  LoaderText,
  ModalContainer,
  ModalContent,
  ModalHeader,
  Overlay,
} from './styled';

// Funções e Componentes de Renderização
const getIcon = (type) => {
  const icons = {
    ok: <CheckCircleIcon />,
    warning: <WarningIcon />,
    error: <ErrorIcon />,
    info: <InfoIcon />,
  };
  return icons[type] || icons.info;
};

const formatTitle = (key) => {
  const replacements = {
    ccb: 'CCB',
    nfprodutos: 'Nota Fiscal de Produtos',
    nfservicos: 'Nota Fiscal de Serviços',
    status: 'Status',
  };
  if (replacements[key]) return replacements[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const AnalysisDetailItem = ({ item }) => {
  const { titulo, mensagem, tipo, key, value } = item;
  return (
    <AnalysisItem>
      <AnalysisIcon type={tipo || 'info'}>{getIcon(tipo)}</AnalysisIcon>
      <AnalysisContent>
        {titulo && <AnalysisTitle>{titulo}</AnalysisTitle>}
        {key && <AnalysisTitle>{formatTitle(key)}</AnalysisTitle>}
        <AnalysisText>
          {mensagem ||
            (value === null || value === ''
              ? 'Não informado.'
              : value.toString())}
        </AnalysisText>
      </AnalysisContent>
    </AnalysisItem>
  );
};

const AnalysisDataRenderer = ({ data }) => {
  if (!data || typeof data !== 'object') {
    return (
      <AnalysisText>
        Não foi possível exibir o resultado da análise. O formato retornado é
        inválido.
      </AnalysisText>
    );
  }
  const hasData = (obj) => {
    return Object.keys(obj).length > 0;
  };
  const renderRecursive = (itemData) => {
    if (typeof itemData !== 'object' || itemData === null) {
      return <AnalysisDetailItem item={{ value: itemData }} />;
    }
    if (itemData.titulo || itemData.mensagem) {
      return <AnalysisDetailItem item={itemData} />;
    }
    if (Array.isArray(itemData)) {
      if (itemData.length === 0) {
        return (
          <AnalysisDetailItem item={{ mensagem: 'Nenhum item encontrado.' }} />
        );
      }
      return itemData.map((item, index) => (
        <div key={index} style={{ marginLeft: '20px' }}>
          {renderRecursive(item)}
        </div>
      ));
    }

    return Object.entries(itemData)
      .map(([key, value]) => {
        if (
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value)
        ) {
          if (!hasData(value)) {
            return null;
          }
          return (
            <div key={key}>
              <AnalysisTitle>{formatTitle(key)}</AnalysisTitle>
              <div style={{ marginLeft: '20px' }}>{renderRecursive(value)}</div>
            </div>
          );
        }
        return <AnalysisDetailItem key={key} item={{ key, value }} />;
      })
      .filter(Boolean);
  };

  return (
    <div>
      {Object.entries(data).map(([key, value]) => (
        <AnalysisSection key={key} keyName={key}>
          <AnalysisSectionTitle>{formatTitle(key)}</AnalysisSectionTitle>
          <AnalysisCard>
            {value !== null && value !== undefined && hasData(value) ? (
              renderRecursive(value)
            ) : (
              <AnalysisDetailItem
                item={{
                  mensagem:
                    'Não foi possível encontrar informações detalhadas para este documento.',
                }}
              />
            )}
          </AnalysisCard>
        </AnalysisSection>
      ))}
    </div>
  );
};

// Componente Principal
export default function ModalAcoesDocumento({
  proposal,
  onClose,
  isClosing = false,
  reloadContent,
}) {
  const [loading, setLoading] = useState(false);
  const [selectedAction, setSelectedAction] = useState('aprovar');
  const [iaResult, setIaResult] = useState(null);
  const [nfProdutoFile] = useState(proposal.nf_venda);
  const [nfServicoFile] = useState(proposal.nf_compra);
  const [ccbFile] = useState(proposal.contrato_financiamento);

  const isbancoOmni = useMemo(() => {
    const bankData = proposal?.bank;
    const aprovedBank =
      bankData?.status === 'aprovado' && bankData?.aceito === true;
    return aprovedBank && isOmni(bankData?.banco);
  }, [proposal]);
  const actionTitles = {
    aprovar: 'Aprovar Documentos',
    //declinar: 'Declinar Documentos',
    reenviar: 'Reenviar Documentos',
    analisar_ia: 'Analisar Documentos com IA',
  };
  const codPropostaMsg = !proposal.codPropostaExterna
    ? proposal.id
    : proposal.codPropostaExterna;
  const successMessages = {
    aprovar: `Proposta ${codPropostaMsg} foi aprovada com sucesso!`,
    //declinar: `Proposta ${proposal.codPropostaExterna} foi declinada com sucesso!`,
    reenviar: `Proposta ${codPropostaMsg} foi marcada para reenvio com sucesso!`,
    analisar_ia: `Proposta ${codPropostaMsg} foi enviada para análise de IA com sucesso!`,
  };
  const handleActionChange = (e) => {
    setSelectedAction(e.target.value);
    setIaResult(null);
  };
  const formatApiResponse = (apiResponse) => {
    if (!apiResponse) return {};
    let formattedObject = Array.isArray(apiResponse)
      ? Object.assign({}, ...apiResponse)
      : { ...apiResponse };
    const normalizeArray = (key) => {
      if (
        formattedObject[key] &&
        typeof formattedObject[key] === 'object' &&
        !Array.isArray(formattedObject[key])
      ) {
        formattedObject[key] = Object.values(formattedObject[key]);
      } else if (formattedObject[key] && !Array.isArray(formattedObject[key])) {
        formattedObject[key] = [formattedObject[key]];
      }
    };
    normalizeArray('nfprodutos');
    normalizeArray('nfservicos');
    normalizeArray('ccb');
    if (formattedObject.status && typeof formattedObject.status !== 'object') {
      formattedObject.status = { value: formattedObject.status };
    }
    return formattedObject;
  };
  const handleSave = async () => {
    if (selectedAction === 'aprovar') {
      const temNfProduto = !!nfProdutoFile?.url;
      const temCcb = !!ccbFile?.url;
      if (!temNfProduto || !temCcb) {
        toast.error(
          `Documentos obrigatórios ausentes: ${!temNfProduto ? '[NF de Produto] ' : ''}${!temCcb ? '[CCB]' : ''}`
        );
        return;
      }
    }
    // if (selectedAction === 'declinar' && !observation.trim()) {
    //   setObservationError(true);
    //   toast.error('A observação é obrigatória para declinar o documento.');
    //   return;
    // }
    // if (observation.length > 500) {
    //   setObservationError(true);
    //   toast.error('A observação não pode exceder 500 caracteres.');
    //   return;
    // }

    // Se a análise de IA já foi feita, o botão de "Salvar" deve se comportar como "Fechar"
    if (selectedAction === 'analisar_ia' && iaResult) {
      onClose();
      return;
    }
    setIaResult(null);
    setLoading(true);
    try {
      const url = '/webhook/attformalizar';
      let body;
      const statusMap = {
        aprovar: 'aprovado',
        // declinar: 'declinada',
        reenviar: 'reenviar',
        analisar_ia: 'julia',
      };
      const newStatus = statusMap[selectedAction];
      //  if (selectedAction === 'declinar') {
      //    body = { proposta: proposal.id, status: newStatus, observacao: observation };
      //  }
      //  else
      if (selectedAction === 'analisar_ia') {
        body = {
          proposta: proposal.id,
          status: newStatus,
          nfprodutos: nfProdutoFile,
          nfservicos: nfServicoFile,
          ccb: ccbFile,
        };
      } else {
        body = { proposta: proposal.id, status: newStatus };
      }
      const result = await POST(url, body, true);
      if (result && result.status === 'ok') {
        toast.success(successMessages[selectedAction]);
        if (selectedAction === 'analisar_ia') {
          const analysisData = result.data || result;
          setIaResult(formatApiResponse(analysisData));
        } else {
          if (reloadContent) reloadContent();
          onClose();
        }
      } else {
        const errorMessage =
          result?.erro || 'Erro desconhecido na resposta da API.';
        toast.error(`Erro ao atualizar a proposta: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Ocorreu um erro na requisição:', error);
      toast.error(`Ocorreu um erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay className={isClosing ? 'closing' : ''} onClick={onClose}>
      <ModalContainer
        className={isClosing ? 'closing' : ''}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <h2>{actionTitles[selectedAction]}</h2>
        </ModalHeader>
        <AddProposalContainer>
          <ModalContent>
            <div>
              <label htmlFor="documentAction">Escolha uma Ação:</label>
              <FormSelect
                id="documentAction"
                value={selectedAction}
                onChange={handleActionChange}
              >
                <option value="aprovar">Aprovar Documentos</option>
                {/* <option value="declinar">Declinar Documentos</option> */}
                <option value="analisar_ia">Analisar com IA</option>
                <option value="reenviar">Recusar Documentos</option>
              </FormSelect>
            </div>
            {/* {selectedAction === 'declinar' && (
              <>
                <label htmlFor="observation">Observação</label>
                <FormTextarea
                  id="observation"
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  error={observationError}
                  placeholder="Digite sua observação aqui..."
                />
                {observationError && (
                  <ErrorMessage>
                    {observation.length > 500 ? 'A observação não pode exceder 500 caracteres.' : 'A observação é obrigatória para declinar.'}
                  </ErrorMessage>
                )}
              </>
            )} */}
            {selectedAction === 'analisar_ia' && (
              <AnalysisResultContainer>
                {loading ? (
                  <>
                    <Loader />
                    <LoaderText>Analisando o documento com IA...</LoaderText>
                  </>
                ) : iaResult ? (
                  <AnalysisDataRenderer data={iaResult} />
                ) : (
                  <p style={{ color: '#555555', textAlign: 'center' }}>
                    Clique em "Salvar" para iniciar a análise.
                  </p>
                )}
              </AnalysisResultContainer>
            )}
          </ModalContent>
        </AddProposalContainer>
        <CompanyButtonContainer>
          <Button onClick={onClose} color="lightgrey" border nospace>
            Cancelar
          </Button>
          <Button onClick={handleSave} loading={loading} color="blue" nospace>
            {loading
              ? 'Processando...'
              : selectedAction === 'analisar_ia' && iaResult
                ? 'Fechar'
                : 'Salvar'}
          </Button>
        </CompanyButtonContainer>
      </ModalContainer>
    </Overlay>
  );
}
